/**
 * useEgoDevice Hook
 *
 * Fetches a single eGO device config by deviceId.
 * Returns { config, isLoading, error, isNotFound }.
 *
 * Requirements: 4.1, 4.2, 5.1, 5.2, 5.4
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchEgoDeviceConfig, EgoApiError } from '../api/egoApiClient';
import { useAuth } from '../../auth/useAuth';
import { getAppConfig } from '../../config/appConfig';
import type { EgoDeviceConfig } from '../models/egoModels';

export interface UseEgoDeviceResult {
  readonly config: EgoDeviceConfig | null;
  readonly lastUpdated: string | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly isNotFound: boolean;
  readonly refetch: () => void;
}

export function useEgoDevice(deviceId: string): UseEgoDeviceResult {
  const [config, setConfig] = useState<EgoDeviceConfig | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);
  const [fetchCount, setFetchCount] = useState(0);
  const { getAccessToken } = useAuth();

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const isInitialLoad = fetchCount === 0;

    async function load() {
      if (isInitialLoad) {
        setIsLoading(true);
      }
      setError(null);
      setIsNotFound(false);

      try {
        const appConfig = getAppConfig();
        const result = await fetchEgoDeviceConfig(deviceId, {
          apiHost: appConfig.apiHost,
          getAccessToken,
        });
        if (!cancelled) {
          if (result === null) {
            setIsNotFound(true);
          } else {
            setConfig(result.config);
            setLastUpdated(result.lastUpdated);
          }
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof EgoApiError
              ? `API error: ${err.status}`
              : 'Failed to load device configuration'
          );
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [deviceId, getAccessToken, fetchCount]);

  return { config, lastUpdated, isLoading, error, isNotFound, refetch };
}
